import { ReactNode, SetStateAction, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import cn from "classnames";
import { produce } from "immer";

import { CheckBox } from "shared/components/Form/CheckBox";
import { WizardButtons } from "shared/components/WizardNew/components/WizardButtons";
import { WizardData } from "shared/components/WizardNew/types";

import { OPTION_DISABLED, OPTION_ENABLED } from "shared/constants/options";
import { SPLIT_TEST } from "shared/constants/split";
import { LOCAL_STORAGE_KEYS } from "shared/constants/storageKeys";
import { DEFAULT_MODE_WIZARD } from "shared/constants/wizard";

import { WizardStore, WizardType } from "shared/context/WizardStoreContext";

import { simplyClick } from "shared/helpers/analytics";
import { isSplitTestRequired } from "shared/helpers/view";
import {
  checkIsContinueAvaliable,
  postWizardData,
  redirectToCurrentStep,
  setStepperState,
} from "shared/helpers/wizard";

import { useLocalStorage } from "shared/hooks/useLocalStorage";
import { useWizard, WizardStep } from "shared/hooks/useWizard";

import { WizardStepper } from "./WizardStepper";

import styles from "@platformAssets/components/Wizard.module.scss";

type Props = {
  children?: ReactNode;
  wizardData: WizardData;
};

const WizardWrapper = ({ children, wizardData }: Props) => {
  const { pathname } = useLocation();

  const navigate = useNavigate();
  const { mode } = wizardData;

  const [wizardState, setWizardState] = useState<WizardType>({
    answers: {},
    isNotProvide: false,
    addPhoto: {
      isPhotoAdded: false,
      isLoading: false,
      isSkippedWithoutPhoto: false,
    },
    stepper: {},
  });

  const [userAvatar] = useLocalStorage(LOCAL_STORAGE_KEYS.USER_AVATAR, null);

  const wizard = useWizard(wizardData);

  const isSplitReducedWizardEnabled =
    mode === DEFAULT_MODE_WIZARD &&
    isSplitTestRequired(wizard.data, SPLIT_TEST.reducedWizard);

  const setWizardData = (data: any) => {
    if ("answers" in data) {
      setWizardState(
        produce((wizard: WizardType) => {
          wizard.answers = data.answers;
        })
      );
    }

    if ("isNotProvide" in data) {
      setWizardState(
        produce(wizardState, (wizard: WizardType) => {
          wizard.isNotProvide = data.isNotProvide;
        })
      );
    }

    if ("isSkippedWithoutPhoto" in data) {
      setWizardState(
        produce(wizardState, (wizard: WizardType) => {
          wizard.addPhoto.isSkippedWithoutPhoto = data.isSkippedWithoutPhoto;
        })
      );
    }
  };

  const { answers, addPhoto, isNotProvide } = wizardState;

  const stepName = pathname.replace("/", "") as WizardStep;

  const step = wizard[stepName];

  const {
    disableWrapper = false,
    nextStep,
    notProvide,
    previousStep,
    skippable,
    analytics,
    title,
    subtitle,
  } = step || {};

  useEffect(() => {
    const block = wizard.data?.reg_funnel_block;

    // Remove after refactoring helpers
    setWizardState(
      setStepperState(
        wizardState,
        block,
        mode,
        isSplitReducedWizardEnabled
      ) as unknown as SetStateAction<WizardType>
    );

    redirectToCurrentStep(wizard, navigate);
  }, [wizardData]);

  useEffect(() => {
    if (isNotProvide) {
      setWizardState((prev) => ({ ...prev, isNotProvide: false }));
    }

    if (pathname.slice(1) === WizardStep.AddPhoto) {
      setWizardState((prev) =>
        produce(prev, (wizard: WizardType) => {
          wizard.addPhoto.isPhotoAdded = Boolean(userAvatar);
        })
      );
    }
  }, [pathname]);

  const handleChangeProvideSelect = (_: string, value: number | null) => {
    setWizardData({ isNotProvide: value });

    if (value && stepName === WizardStep.DateAge) {
      setWizardData({
        answers: { [WizardStep.DateAge]: { age_from: 18, age_to: 90 } },
      });
    }

    if (analytics?.notProvide) {
      analytics?.notProvide?.forEach((item: string) => {
        simplyClick(item, null, false);
      });
    }
  };

  const handlePrevious = () => {
    navigate(`/${previousStep}`);

    analytics?.back?.forEach((item: string) => {
      simplyClick(item, null, false);
    });

    if (pathname.slice(1) === WizardStep.AddPhoto) {
      if (addPhoto.isPhotoAdded) {
        analytics?.addedBack?.forEach((item: string) => {
          simplyClick(item, null, false);
        });
      }

      if (addPhoto.isLoading) {
        analytics?.loadingBack?.forEach((item: string) => {
          simplyClick(item, null, false);
        });
      }
    }
  };

  const handleSkip = () => {
    analytics?.skip?.forEach((item: string) => {
      simplyClick(item, null, false);
    });

    if (pathname.slice(1) === WizardStep.AddPhoto) {
      if (isSplitReducedWizardEnabled) {
        setWizardData({
          isSkippedWithoutPhoto: false,
        });
        navigate(`/${nextStep}`);
      } else {
        setWizardData({
          isSkippedWithoutPhoto: !addPhoto.isPhotoAdded,
        });

        if (addPhoto.isPhotoAdded) {
          analytics?.addedSkip?.forEach((item: string) => {
            simplyClick(item, null, false);
          });
          navigate(`/${nextStep}`);
        }
      }
    } else {
      setWizardState(
        produce((wizard: WizardType) => {
          wizard.answers[stepName] = null;
        })
      );

      navigate(`/${nextStep}`);
    }
  };

  const isContinueAvaliable = checkIsContinueAvaliable(
    isNotProvide,
    step,
    answers,
    stepName,
    addPhoto.isPhotoAdded
  );

  const handleSend = async () => {
    await postWizardData(answers, pathname.slice(1));

    navigate(`/${nextStep}`);

    const nextParams =
      stepName === WizardStep.Interests
        ? { interests: JSON.stringify(answers[stepName]) }
        : null;

    analytics?.next?.forEach((item: string) => {
      simplyClick(item, nextParams, false);
    });

    analytics?.addedNext?.forEach((item: string) => {
      simplyClick(item, null, false);
    });
  };

  return (
    <WizardStore.Provider value={[wizardState, setWizardState]}>
      {disableWrapper ? (
        children
      ) : (
        <div
          className={cn(styles.wizardWrapper, {
            [styles.interestsWrap]: stepName === WizardStep.Interests,
          })}
        >
          {!isSplitReducedWizardEnabled && <WizardStepper mode={mode} />}

          <div
            className={cn(styles.wizardContent, {
              [styles.addPhotoStep]: stepName === WizardStep.AddPhoto,
              [styles.interests]: stepName === WizardStep.Interests,
            })}
          >
            <div>
              {title && <div className={styles.wizardTitle}>{title}</div>}
              {subtitle && (
                <div className={styles.wizardSubTitle}>{subtitle}</div>
              )}

              {children}

              {notProvide ? (
                <div className={styles.wizardNotProvide}>
                  <CheckBox
                    label={notProvide.text}
                    name="not_provide"
                    handleChange={handleChangeProvideSelect}
                    value={isNotProvide ? OPTION_ENABLED : OPTION_DISABLED}
                    checkedValue={OPTION_ENABLED}
                  />
                </div>
              ) : null}
            </div>

            <WizardButtons
              variant="wizard"
              showBack={Boolean(previousStep)}
              showNext={Boolean(nextStep)}
              showSkip={Boolean(skippable)}
              isContinueAvailable={isContinueAvaliable}
              onBack={handlePrevious}
              onNext={handleSend}
              onSkip={handleSkip}
              userData={wizardData}
            />
          </div>
        </div>
      )}
    </WizardStore.Provider>
  );
};

export { WizardWrapper };
